const storageKey = 'add-to-home-screen-prompt'

// Days to wait before prompting again, one entry per dismissal. Once the user has
// dismissed the prompt more times than there are entries they aren't asked again,
// leaving the primary nav link as the only way in
const snoozeDays = [7, 30]

const dayInMs = 24 * 60 * 60 * 1000

const getPromptState = (): AddToHomeScreenPrompt => {
  try {
    const stored = localStorage.getItem(storageKey)
    return stored ? JSON.parse(stored) : { dismissals: 0, lastDismissedAt: 0 }
  } catch {
    return { dismissals: 0, lastDismissedAt: 0 }
  }
}

export default function useAddToHomeScreen() {
  // iPadOS 13+ reports itself as a Mac, so touch points are checked as well
  const isAppleDevice = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)

  // `navigator.standalone` is the Safari equivalent of the standalone display mode
  const isInstalled = () => window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true

  const isSnoozed = () => {
    const { dismissals, lastDismissedAt } = getPromptState()

    if (!dismissals) return false

    const waitDays = snoozeDays[dismissals - 1]

    // Dismissed as many times as we're prepared to ask, so never prompt again
    if (waitDays === undefined) return true

    return Date.now() - lastDismissedAt < waitDays * dayInMs
  }

  // Apple devices only support push notifications once the app has been added to
  // the home screen, so the instructions are only of use to users who haven't yet
  const canAddToHomeScreen = () => isAppleDevice() && !isInstalled()

  const showAddToHomeScreen = () => canAddToHomeScreen() && !isSnoozed()

  const dismissAddToHomeScreen = () => {
    try {
      const { dismissals } = getPromptState()

      localStorage.setItem(storageKey, JSON.stringify({ dismissals: dismissals + 1, lastDismissedAt: Date.now() }))
    } catch {
      // Nothing to do - the prompt just isn't snoozed on this device
    }
  }

  return {
    canAddToHomeScreen,
    showAddToHomeScreen,
    dismissAddToHomeScreen
  }
}
